import React, { useEffect, useState } from 'react';
import { Modal, Card, Tag, Typography, Space, Spin, Empty } from 'antd';
import { ShopOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getRegistroTransmision } from '../../api/mejoraCU2Api';
import type { ReconciliacionTransmision } from '../../api/reconciliacionApi';

const { Text } = Typography;

interface Props {
  casoId: number | null;
  titulo?: string;
  open: boolean;
  onClose: () => void;
}

/** Popup de solo lectura con la información principal de la transmisión vinculada al caso. */
const RegistroTransmisionModal: React.FC<Props> = ({ casoId, titulo, open, onClose }) => {
  const [data, setData] = useState<ReconciliacionTransmision | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || casoId == null) return;
    let cancel = false;
    setLoading(true);
    setData(null);
    getRegistroTransmision(casoId)
      .then(r => { if (!cancel) setData(r); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, casoId]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={720} title={titulo ?? 'Transmisión vinculada'}>
      {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
      {!loading && !data && <Empty description="Sin datos del registro vinculado" />}
      {!loading && data && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              Transmisión #{data.id} · patrón {data.patron}
              {data.fecha && ` · ${new Date(data.fecha).toLocaleDateString('es-ES')}`}
            </Text>
          </div>

          <Card size="small" style={{ marginBottom: 12 }} title={<span><ShopOutlined style={{ marginRight: 8 }} />Farmacia</span>}>
            <div><Text strong>{data.farmacia?._Name || `ID ${data.farmaciaId}`}</Text></div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">ID Gescom: {data.farmaciaId}</Text>
              {data.farmacia?.provincia && <Text type="secondary"> · {data.farmacia.provincia}</Text>}
            </div>
          </Card>

          {data.vendedores?.length > 0 && (
            <Card
              size="small"
              style={{ marginBottom: 12 }}
              title={<span><ArrowRightOutlined style={{ color: '#cf1322', marginRight: 8 }} />Titulares salientes (CT)</span>}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {data.vendedores.map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>{v.cliente?._Name || `ID ${v.vendedorId}`}</Text>
                    <Space size={6}>
                      {v.fechaFin && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(v.fechaFin).toLocaleDateString('es-ES')}</Text>}
                      {v.excepcionE2 && <Tag color="red">E2</Tag>}
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {data.compradores?.length > 0 && (
            <Card
              size="small"
              title={<span><ArrowLeftOutlined style={{ color: '#389e0d', marginRight: 8 }} />Titulares entrantes (AT)</span>}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {data.compradores.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>{c.cliente?._Name || `ID ${c.compradorId}`}</Text>
                    <Space size={6}>
                      {c.fechaInicio && <Text type="secondary" style={{ fontSize: 12 }}>{new Date(c.fechaInicio).toLocaleDateString('es-ES')}</Text>}
                      {c.excepcionE1 && <Tag color="red">E1</Tag>}
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          )}
        </>
      )}
    </Modal>
  );
};

export default RegistroTransmisionModal;
